<template>
  <div ref="menuButtonWrapperRef">
    <template v-if="$slots['menu-button-trigger']">
      <div class="custom-trigger" :class="{ 'full-width': fullWidth }" ref="menuButtonRef" @click="toggle">
        <slot name="menu-button-trigger" />
      </div>
    </template>

    <Button
      v-else
      variant="outline"
      :class="{ 'full-width': fullWidth }"
      :size="buttonSize"
      v-bind="$attrs"
      ref="menuButtonRef"
      @click="toggle"
    >
      <View class="button-prefix" gap="sm" direction="row" align="center" :grow="1">
        <slot name="trigger-button-prefix" />
        <span>{{ buttonLabel }}</span>
      </View>
      <Icon name="chevron-down" />
    </Button>

    <dialog class="dialog" ref="dialogRef" :style="floatingStyles">
      <Listbox>
        <slot />
      </Listbox>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { autoPlacement, autoUpdate, offset, ReferenceElement, size, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { computed, provide, ref, useTemplateRef } from 'vue';

import { screenMinSm } from '../../utils/tallo-breakpoints.util';
import { ButtonProps } from '../button/button-props.interface';
import Button from '../button/button.vue';
import Icon from '../icon.vue';
import Listbox from '../listbox/listbox.vue';
import View from '../view/view.vue';
import { menuButtonInjectionKey } from './menu-button.token';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<{ buttonLabel?: string; fullWidth?: boolean } & ButtonProps>(), {
  size: 'large',
  fullWidth: false,
});

const dialogRef = ref<HTMLDialogElement | null>(null);
const menuButtonWrapperRef = useTemplateRef<HTMLElement>('menuButtonWrapperRef');
const menuButtonRef = useTemplateRef<ReferenceElement>('menuButtonRef');
const buttonSize = computed(() => (screenMinSm.value ? props.size : 'medium'));

provide(menuButtonInjectionKey, {
  close,
});

onClickOutside(menuButtonWrapperRef, close);

const { floatingStyles } = useFloating(menuButtonRef, dialogRef, {
  transform: false,
  strategy: 'absolute',
  whileElementsMounted: autoUpdate,
  placement: 'bottom-end',
  middleware: [
    offset(8),
    autoPlacement({
      crossAxis: true,
      allowedPlacements: ['bottom-start', 'bottom-end', 'top-start', 'top-end'],
    }),
    size({
      apply({ availableWidth, availableHeight, elements, rects }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
          maxWidth: `${Math.max(0, availableWidth)}px`,
          maxHeight: `${Math.max(0, availableHeight)}px`,
        });
      },
    }),
  ],
});

function toggle() {
  if (props.disabled) {
    return;
  }

  dialogRef.value?.open ? close() : show();
}

function show() {
  dialogRef.value?.show();
}

function close() {
  dialogRef.value?.close();
}

defineExpose({
  close,
});
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.full-width {
  width: 100%;
}

.custom-trigger {
  display: inline-block;
}

.dialog {
  margin: 0;
  padding: 0;
  border: none;
  border-radius: var(--t-border-radius-sm);
  box-shadow: var(--t-shadow-md);

  min-width: 16rem;
  z-index: 1;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
